import React, {useState} from 'react';
import {Alert, Card, Form} from 'react-bootstrap';
import {LoadingButton} from 'components/ui/LoadingButton';
import {useAppDispatch} from 'redux/store';
import {useProfile} from 'utils/useProfile';
import {userEndpoints} from 'services/user/slice';

const firstMessage = (err: any): string => {
  if (err && typeof err === 'object') {
    if (Array.isArray(err.password) && err.password[0]) return String(err.password[0]);
    if (typeof err.detail === 'string') return err.detail;
  }
  return 'Could not set your password.';
};

/**
 * Blocks the app for a user who signed in with an admin-issued temporary password until they
 * choose one of their own. Rendered instead of the routes, so there is nothing to navigate
 * around -- the temporary password cannot become the account's permanent one.
 */
export const SetPasswordGate = (): React.ReactElement => {
  const appDispatch = useAppDispatch();
  const {loadProfile} = useProfile();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await appDispatch(userEndpoints.setPassword({password})).unwrap();
      // Clears must_change_password, which is what lets the app render.
      await loadProfile();
    } catch (e) {
      setError(firstMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center" style={{minHeight: '100vh'}}>
      <Card style={{maxWidth: '28rem', width: '100%'}} className="p-4">
        <h4 className="mb-3">Choose a password</h4>
        <p className="text-muted small">
          You signed in with a temporary password. Pick a password of your own to continue.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={onSubmit}>
          <Form.Group className="mb-3" controlId="new_password">
            <Form.Label>New password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="confirm_password">
            <Form.Label>Confirm password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
            />
          </Form.Group>
          <LoadingButton loading={saving} type="submit" variant="primary" className="w-100">
            Set password
          </LoadingButton>
        </Form>
      </Card>
    </div>
  );
};

export default SetPasswordGate;
